/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import * as queryString from "query-string";
import { useHistory, useLocation } from "react-router-dom";
import { AuditFiltersProp } from "~/components/Audit/SearchNavbar";
import { FiltersProp } from "~/components/Filters/types";

type SetValue = number | string | undefined | FiltersProp | AuditFiltersProp;

export function getTypedKeys<T>(obj: T): Array<keyof T> {
  return Object.keys(obj) as Array<keyof typeof obj>;
}

export type QueryState = FiltersProp & {
  page?: string;
  limit?: string;
};

type QueryStateKey = keyof QueryState;

function useUpdateUrl() {
  const { pathname } = useLocation();
  const history = useHistory();

  const updateURL = (queryState: QueryState) => {
    return history.push(
      pathname +
        "?" +
        queryString.stringify(queryState, {
          arrayFormat: "index",
        })
    );
  };

  return { updateURL };
}

export function useQueryState<
  T extends queryString.ParsedQuery<string | number | boolean>
>() {
  const { search } = useLocation();
  const [queryState, setQueryState] = React.useState<QueryState & T>(
    () =>
      queryString.parse(search, {
        arrayFormat: "index",
        parseBooleans: true,
        parseNumbers: true,
      }) as QueryState & T
  );

  const { updateURL } = useUpdateUrl();

  React.useEffect(() => {
    setQueryState(
      queryString.parse(search, {
        arrayFormat: "index",
        parseBooleans: true,
        parseNumbers: true,
      }) as QueryState & T
    );
  }, [search]);

  const set = (field: QueryStateKey | keyof T) => (value: SetValue) => {
    if (typeof value === "object") {
      const { limit } = queryState;
      const isLimit = limit ? limit : "20";
      const newState = { page: "0", limit: isLimit, ...value };
      updateURL(newState);
    } else {
      updateURL({ ...queryState, [field]: value });
    }
  };

  const updateQueryState = (fields: QueryState & T) => {
    updateURL({ ...queryState, ...fields });
  };

  const removeFields = (fields: (QueryStateKey | keyof T)[]) => {
    const fieldsToKeep = getTypedKeys(queryState).filter(
      (key) => fields.includes(key) === false
    );
    const savedQueryState = fieldsToKeep.reduce((acc, curr) => {
      return {
        ...acc,
        [curr]: queryState[curr],
      };
    }, {});

    updateURL(savedQueryState);
  };

  return [
    {
      ...queryState,
      limit: parseInt(queryState.limit ?? "20", 10),
      page: parseInt(queryState.page ?? "0", 10),
    },
    set,
    updateQueryState,
    removeFields,
  ] as const;
}
